import React, { ReactNode } from 'react'
import CssBaseline from '@mui/material/CssBaseline'
import GlobalStyles from '@mui/material/GlobalStyles'
import {
  ThemeProvider,
  createTheme,
  responsiveFontSizes,
} from '@mui/material/styles'
import { ruRU, esES, enUS } from '@mui/material/locale'
import { useTranslation } from 'react-i18next'

import themeConfig from 'src/theme/themeConfig'
import themeOptions from './ThemeOptions'
import GlobalStyling from './globalStyles'
import { useSettings } from 'src/hooks/useSettings'
import { useAppearance } from 'src/context/appearanceContext'
import type { SupportedLanguage } from 'src/locale/i18n'

const muiLocales: Record<SupportedLanguage, typeof enUS> = {
  en: enUS,
  ru: ruRU,
  es: esES,
}

interface Props {
  children: ReactNode
}

const ThemeComponent = (props: Props) => {
  const { children } = props
  const { settings } = useSettings()
  const { resolvedMode } = useAppearance()
  const { i18n } = useTranslation()

  const currentLocale = muiLocales[i18n.language as SupportedLanguage] || enUS

  const theme = React.useMemo(() => {
    let built = createTheme(
      themeOptions({ ...settings, mode: resolvedMode }, resolvedMode),
      currentLocale
    )
    if (themeConfig.responsiveFontSizes) {
      built = responsiveFontSizes(built)
    }

    return built
  }, [settings, resolvedMode, currentLocale])

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline enableColorScheme />
      <GlobalStyles styles={() => GlobalStyling(theme) as any} />
      {children}
    </ThemeProvider>
  )
}

export default ThemeComponent
